import { logger } from "@/lib/logger";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart3, Lock, ArrowLeft, CheckCircle2, AlertCircle, Eye, EyeOff, Calendar, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setErrorMessage("No reset token provided");
        setIsVerifying(false);
        return;
      }

      try {
        const response = await fetch(`${API}/auth/verify-reset-token?token=${token}`);
        
        if (response.ok) {
          const data = await response.json();
          setIsValidToken(true);
          setMaskedEmail(data.email);
        } else {
          const error = await response.json();
          setErrorMessage(error.detail || "Invalid or expired reset token");
        }
      } catch (error) {
        logger.error("Token verification error:", error);
        setErrorMessage("Failed to verify reset token");
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (response.ok) {
        setIsSuccess(true);
        toast.success("Password reset successful!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reset password");
      }
    } catch (error) {
      logger.error("Reset password error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="spinner border-violet-500 border-t-transparent w-8 h-8 mx-auto mb-4" />
          <p className="text-zinc-400">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center">
            <img src="/roas-ai-logo.png" alt="ROAS-AI" className="w-10 h-10 object-contain" />
          </div>
          <span className="font-heading text-xl text-white font-bold">ROAS-AI</span>
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-2xl p-8">
          <AnimatePresence mode="wait">
            {/* Error State - Invalid Token */}
            {!isValidToken && !isSuccess && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="font-heading text-2xl text-white font-bold mb-2">
                  Invalid Reset Link
                </h2>
                <p className="text-zinc-400 text-sm mb-6">
                  {errorMessage || "This password reset link is invalid or has expired."}
                </p>
                <Button
                  onClick={() => navigate("/forgot-password")}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                >
                  Request New Link
                </Button>
              </motion.div>
            )}

            {/* Reset Form */}
            {isValidToken && !isSuccess && (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-violet-500/10 flex items-center justify-center">
                    <Lock className="w-8 h-8 text-violet-400" />
                  </div>
                  <h2 className="font-heading text-2xl text-white font-bold mb-2">
                    Reset Your Password
                  </h2>
                  <p className="text-zinc-400 text-sm">
                    Create a new password for <span className="text-white">{maskedEmail}</span>
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-zinc-400">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-300"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-zinc-500 text-xs">Minimum 6 characters</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-zinc-400">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg py-5"
                    data-testid="reset-password-btn"
                  >
                    {isLoading ? (
                      <div className="spinner border-white border-t-transparent" />
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </form>
              </motion.div>
            )}

            {/* Success State */}
            {isSuccess && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="font-heading text-2xl text-white font-bold mb-2">
                  Password Reset!
                </h2>
                <p className="text-zinc-400 text-sm mb-6">
                  Your password has been successfully reset. You can now log in with your new password.
                </p>
                <Button
                  onClick={() => navigate("/login")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="go-to-login-btn"
                >
                  Go to Sign In
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Back to login */}
          {!isSuccess && (
            <div className="mt-6 pt-6 border-t border-white/5 text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-zinc-400 hover:text-white text-sm transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sign In
              </Link>
            </div>
          )}

          {/* Fast-track approval link */}
          {!isSuccess && (
            <div className="mt-4 text-center">
              <a
                href="https://calendar.app.google/gbZSzz4TiFAYYSjL8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-emerald-400 hover:text-emerald-300 text-sm transition-colors"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule a call for fast-track approval
                <ExternalLink className="w-3 h-3 ml-1" />
              </a>
            </div>
          )}
        </div>
      </motion.div>
      <p className="text-zinc-500 text-xs text-right mt-6">Copyright (c) 2026 Beruf LLC</p>
    </div>
  );
}
